import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Chat as ChatIcon } from "../../icons";
import { useNavigate, useParams } from "react-router-dom";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import PayPalSurface from "../../components/PayPalSurface";
import firebase from "firebase/compat/app";
import { auth, db, functions } from "../../firebase";
import { notifyUser } from "../../utils/notifications";
import StoresAppBar from "./StoresAppBar";
import DiscountPriceBreakdown from "../../components/DiscountPriceBreakdown";
import { T } from "../Translate";
import { pageContentSx } from "../../pageLayout";
import { brandColors } from "../../theme";

const ProductCheckout = () => {
  const { storeSlug, productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [chatConversationId, setChatConversationId] = useState(null);

  const [storeContact, setStoreContact] = useState({ ownerId: '', storeName: '', ownerEmail: '', whatsapp: '', address: '', hours: '', paypalEmail: '' });

  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storeRef = db.collection("stores").doc(storeSlug);
        const [storeSnap, productSnap] = await Promise.all([
          storeRef.get(),
          storeRef.collection("products").doc(productId).get(),
        ]);

        if (storeSnap.exists) {
          const data = storeSnap.data();
          setStoreContact({
            ownerId: data.ownerId || '',
            storeName: data.storeName || '',
            ownerEmail: data.ownerEmail || '',
            whatsapp: data.whatsapp || '',
            address: data.address || '',
            hours: data.hours || '',
            paypalEmail: data.paypalEmail || '',
          });
        }

        if (productSnap.exists) {
          setProduct(productSnap.data());
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };

    fetchData();
  }, [storeSlug, productId]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: "80px" }}>
        <StoresAppBar title={<T>Finalizar compra</T>} quickNav={false} maxWidth="sm" />
        <Stack alignItems="center" sx={{ mt: 10 }}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: "80px" }}>
        <StoresAppBar title={<T>Finalizar compra</T>} quickNav={false} maxWidth="sm" />
        <Container sx={{ mt: 10 }}>
          <Alert severity="error"><T>Producto no encontrado</T></Alert>
        </Container>
      </Box>
    );
  }

  // product.price es siempre el precio de lista (ver "Precio en USD" en el
  // formulario del vendedor en StoreDetail.js). El cupón, si se aplica, resta
  // el porcentaje sobre ese precio para dar el total a cobrar.
  const regularPrice = Number(product.price) || 0;
  const discountRate = appliedCoupon ? appliedCoupon.rate : 0;
  const discountedPrice = discountRate > 0
    ? regularPrice * (1 - discountRate)
    : regularPrice;
  const finalTotal = discountedPrice.toFixed(2);

  const handleApplyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    const productCouponCode = product.couponCode ? product.couponCode.toUpperCase() : null;
    const rate = productCouponCode && code === productCouponCode
      ? Number(product.couponDiscount) / 100
      : null;

    if (!rate) {
      setAppliedCoupon(null);
      setCouponMessage({
        type: "error",
        text: <T>Cupón no válido</T>,
      });
      return;
    }

    setAppliedCoupon({ code, rate });
    setCouponMessage({
      type: "success",
      text: <T>Cupón aplicado ({rate * 100}% de descuento)</T>,
    });
  };

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            currency_code: "USD",
            value: finalTotal,
          },
          // El pago va directo al PayPal del vendedor, no al de la plataforma.
          payee: { email_address: storeContact.paypalEmail },
          // Con esto recordPayPalPayment comprueba que el pago es de esta tienda.
          custom_id: storeSlug,
        },
      ],
    });
  };

  const onApprove = (data, actions) => {
    return actions.order.capture().then(async (details) => {
      try {
        const buyerUid = currentUser?.uid || null;
        const sellerUid = storeContact.ownerId;
        const serverTimestamp = firebase.firestore.FieldValue.serverTimestamp();

        if (buyerUid && sellerUid) {
          const conversationId = `tienda_${storeSlug}_${buyerUid}`;
          const participants = [buyerUid, sellerUid];
          const conversationRef = db.collection("conversations").doc(conversationId);
          const conversationSnap = await conversationRef.get();
          if (!conversationSnap.exists) {
            await conversationRef.set({
              type: "tienda",
              storeId: storeSlug,
              storeName: storeContact.storeName,
              participants,
              createdAt: serverTimestamp,
              lastMessageTime: serverTimestamp,
              unreadCount: { [buyerUid]: 0, [sellerUid]: 0 },
            });
          }

          await notifyUser({
            userId: sellerUid,
            companyId: buyerUid,
            category: "messages",
            type: "message",
            message: `Nuevo pedido: ${product.name}`,
            postId: conversationId,
          });

          setChatConversationId(conversationId);
        }

        // La venta la guarda recordPayPalPayment después de verificar el
        // pago con PayPal (monto real cobrado, vendedor y producto).
        await functions.httpsCallable("recordPayPalPayment")({
          kind: "storeSale",
          targetId: storeSlug,
          orderId: details.id,
          extra: { productId, coupon: appliedCoupon?.code || null },
        });

        setPaymentMessage({
          type: "success",
          text: <T>Pago completado correctamente</T>,
        });
      } catch (error) {
        console.error(error);
        setPaymentMessage({
          type: "warning",
          text: <T>Pago realizado pero no guardado</T>,
        });
      }
    });
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar
        title={<T>Finalizar compra</T>}
        subtitle={product.name}
        quickNav={false}
        maxWidth="lg"
      />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, py: { xs: 4, sm: 8 } }}>
        <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
            <T>Finalizar compra</T>
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 3, sm: 5 }} alignItems="flex-start">
            <Stack spacing={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <Typography fontWeight={600}>
                {product.name}
              </Typography>

              {appliedCoupon ? (
                <DiscountPriceBreakdown
                  originalPrice={regularPrice}
                  discountPercent={appliedCoupon.rate * 100}
                  finalPrice={finalTotal}
                />
              ) : (
                <Typography fontWeight={700}>
                  <T>Precio: ${regularPrice.toFixed(2)}</T>
                </Typography>
              )}

              {product.stock != null && (
                <Typography variant="body2" sx={{ color: product.stock > 0 ? "text.secondary" : "error.main" }}>
                  {product.stock > 0 ? <T>Stock disponible: {product.stock}</T> : <T>Producto agotado</T>}
                </Typography>
              )}

              <Divider />

              {/* 🔥 CUPÓN */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={<T>Cupón</T>}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={handleApplyCoupon}
                  sx={{ flexShrink: 0 }}
                >
                  <T>Aplicar</T>
                </Button>
              </Stack>

              {couponMessage && (
                <Alert severity={couponMessage.type}>
                  {couponMessage.text}
                </Alert>
              )}
            </Stack>

            <Stack spacing={2} sx={{ width: { xs: "100%", sm: "55%" } }}>
              {paymentMessage && (
                <>
                  <Alert severity={paymentMessage.type}>
                    {paymentMessage.text}
                  </Alert>
                  {paymentMessage.type === 'success' && (
                    <Paper sx={{ p: 2, background: brandColors.backgroundDefault, color: brandColors.textPrimary }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        <T>Datos de contacto y tienda del vendedor</T>
                      </Typography>
                      {storeContact.ownerEmail && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b><T>Email:</T></b> {storeContact.ownerEmail}
                        </Typography>
                      )}
                      {storeContact.whatsapp && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b><T>WhatsApp:</T></b> {storeContact.whatsapp}
                        </Typography>
                      )}
                      {storeContact.address && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b><T>Dirección:</T></b> {storeContact.address}
                        </Typography>
                      )}
                      {storeContact.hours && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <b><T>Horarios de atención:</T></b> {storeContact.hours}
                        </Typography>
                      )}
                      {!(storeContact.ownerEmail || storeContact.whatsapp || storeContact.address || storeContact.hours) && (
                        <Typography variant="body2"><T>El vendedor no ha configurado datos de contacto públicos.</T></Typography>
                      )}
                    </Paper>
                  )}
                  {chatConversationId && (
                    <Button
                      variant="contained"
                      startIcon={<ChatIcon />}
                      onClick={() => navigate(`/chatbox/directo/${storeContact.ownerId}`)}
                    >
                      <T>Abrir chat con el vendedor</T>
                    </Button>
                  )}
                </>
              )}

              {!paymentMessage && (
                <>
                  <Alert severity="info" icon={<ChatIcon fontSize="inherit" />}>
                    <T>Al completar la compra se abrirá un chat con el vendedor para coordinar el envío y los detalles de tu pedido.</T>
                  </Alert>

                  {!authChecked ? (
                    <Stack alignItems="center" sx={{ py: 2 }}>
                      <CircularProgress size={28} />
                    </Stack>
                  ) : !currentUser ? (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: brandColors.divider }}>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <T>Inicia sesión para completar la compra y chatear con el vendedor.</T>
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <Button variant="contained" onClick={() => navigate("/login")}>
                          <T>Iniciar sesión</T>
                        </Button>
                        <Button variant="outlined" onClick={() => navigate("/register")}>
                          <T>Registrarse</T>
                        </Button>
                      </Stack>
                    </Paper>
                  ) : !storeContact.paypalEmail ? (
                    <Alert severity="error">
                      <T>Esta tienda todavía no configuró un correo de PayPal para recibir pagos. No se puede completar la compra por ahora.</T>
                    </Alert>
                  ) : (
                    <PayPalScriptProvider
                      options={{
                        "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                        currency: "USD",
                      }}
                    >
                      <PayPalSurface>
                        <PayPalButtons
                          createOrder={createOrder}
                          onApprove={onApprove}
                          style={{
                            layout: "vertical",
                            color: "blue",
                            shape: "rect",
                            label: "pay",
                            height: 45,
                          }}
                        />
                      </PayPalSurface>
                    </PayPalScriptProvider>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProductCheckout;
